import type { FC } from 'react';
import {
  ActionableNotification,
  Button,
  Dropdown,
  InlineLoading,
  InlineNotification,
  Tag,
} from '@carbon/react';
import { CheckmarkOutline, TrashCan } from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import type {
  PreDrillWorkspace,
  WellLandingAnalysisInstance,
} from 'types/api-responses';
import type { WellborePointsResponse } from 'types/wellbore-geometry-workspace';
import { useLandingInterpretation } from './hooks';
import { IntervalCardDeck } from './interval-card-deck';
import { isManualRun, runLabel, runSourceLabel } from './utils';
import type {
  LandingCollectionOption,
  LandingIntervalPickerRequest,
} from './types';
import styles from './landing-interpretation-section.module.scss';

interface LandingInterpretationSectionProps {
  workspace: PreDrillWorkspace;
  wellWorkspaceId: number;
  points: WellborePointsResponse | null;
  pointsHaveDepths: boolean;
  /** True while the points read is in flight: availability waits on it. */
  pointsLoading?: boolean;
  /** Re-read the bore show after Mark as Correct / Remove Correct Status. */
  onChanged: () => void;
  /** Open the Select Geologic Interval drawer in the host's right lane. */
  onOpenIntervalPicker: (request: LandingIntervalPickerRequest) => void;
}

const renderCollection = (item: LandingCollectionOption | null) => {
  if (!item) return <span />;
  return (
    <span className={styles.option}>
      {item.name}
      {item.isDefault && (
        <Tag size="sm" type="gray">
          Default
        </Tag>
      )}
    </span>
  );
};

const renderRun = (item: WellLandingAnalysisInstance | null) => {
  if (!item) return <span />;
  return (
    <span className={styles.option}>
      {runLabel(item)}
      {/* Review item 7.2: which geometry the run was computed on. */}
      <span className={styles.optionSource}>{runSourceLabel(item)}</span>
      {isManualRun(item) && (
        <Tag size="sm" type="blue">
          Manual
        </Tag>
      )}
    </span>
  );
};

// Landing Analysis Interpretation (INS-12936): which geologic interval this wellbore is attributed
// to. Picking a card or a catalog interval stages a reading; Mark as Correct applies it to the
// workspace; the workspace promote publishes it as the well's landing. Never edits the wellbore
// geometry or location.
//
// Keyed by the bore (review): the host keeps this mounted when it switches to a bore it has already
// loaded, and the staged pick, chosen run and a failed write all belong to the bore they were made on.
export const LandingInterpretationSection: FC<
  LandingInterpretationSectionProps
> = (props) => (
  <BoreLandingInterpretation key={props.workspace.uwi14} {...props} />
);

const BoreLandingInterpretation: FC<LandingInterpretationSectionProps> = ({
  workspace,
  wellWorkspaceId,
  points,
  pointsHaveDepths,
  pointsLoading = false,
  onChanged,
  onOpenIntervalPicker,
}) => {
  const state = useLandingInterpretation({
    workspace,
    wellWorkspaceId,
    points,
    pointsHaveDepths,
    pointsLoading,
    onChanged,
  });
  const {
    availability,
    readOnly,
    banner,
    applied,
    selectedId,
    busy,
    runDetail,
    run,
  } = state;
  const ready = availability === 'ready';
  const appliedId = applied?.geologicIntervalId ?? null;

  // A catalog read that failed is retried as the picker opens, and from its Retry.
  const openCatalog = () => {
    if (state.intervalsFailed) state.refetchIntervals();
    onOpenIntervalPicker({
      selectedId,
      hasGridRun: state.hasGridRun,
      intervalsAtom: state.intervalsAtom,
      onPick: state.stageInterval,
      onRetry: state.refetchIntervals,
    });
  };

  // A run whose landingParameters is {} (or lacks a key) reads as unknown, never as 0° or "No".
  const parameters: Partial<
    NonNullable<typeof runDetail>['landingParameters']
  > = runDetail?.landingParameters ?? {};
  const known = <T,>(value: T | null | undefined, render: (v: T) => string) => {
    if (value === null || value === undefined) return '—';
    return render(value);
  };
  const lead = ready && run && (
    <div className={styles.leadCard}>
      <span className={styles.tileEyebrow}>Landing Analysis</span>
      <p className={styles.tileName}>{runLabel(run)}</p>
      <dl className={styles.tileFields}>
        {[
          [
            'Structure Grid Collection',
            run.structureGridCollection?.collectionName ?? '—',
          ],
          ['Geometry', runSourceLabel(run)],
          [
            'Inclination Cutoff',
            known(
              parameters.horizontalInclinationCutoffDeg,
              (v) => `${formatNumber(v, 0, 1)}°`
            ),
          ],
          [
            'Well Segment Step Frequency',
            known(
              parameters.stepLengthFt,
              (v) => `${formatNumber(v, 0, 1)} ft`
            ),
          ],
          [
            'Maximum Thickness',
            known(
              parameters.thicknessCutoffFt,
              (v) => `${formatNumber(v, 0, 1)} ft`
            ),
          ],
          [
            'Use Perforation Geometry',
            known(parameters.usePerforations, (v) => (v ? 'Yes' : 'No')),
          ],
        ].map(([label, value]) => (
          <div key={label} className={styles.tileField}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  return (
    <section
      className={styles.section}
      aria-labelledby="landing-interpretation-title"
    >
      <div className={styles.header}>
        <h2 id="landing-interpretation-title" className={styles.title}>
          Landing Analysis Interpretation
        </h2>
        {/* No-depths has nothing to confirm, so the actions only appear where a pick can be made.
            Both stay put and disable out of state, the two directions of one decision. */}
        {!state.loading && availability !== 'no-depths' && (
          <div className={styles.headerActions}>
            <Button
              kind="danger--tertiary"
              size="sm"
              hasIconOnly
              renderIcon={TrashCan}
              iconDescription="Remove Correct Status"
              tooltipPosition="bottom"
              tooltipAlignment="end"
              disabled={readOnly || appliedId === null || busy}
              onClick={state.removeCorrectStatus}
            />
            <Button
              kind="primary"
              size="sm"
              renderIcon={CheckmarkOutline}
              iconDescription="Mark as Correct"
              disabled={!state.canMark || busy}
              onClick={state.markAsCorrect}
            >
              Mark as Correct
            </Button>
          </div>
        )}
      </div>

      <div className={styles.body}>
        <p className={styles.description}>
          Which geologic interval this wellbore is attributed to. It is
          interpreted from the wellbore geometry and the structure grids: saving
          an interpretation never changes the wellbore geometry or location.
        </p>

        {state.loading ? (
          <InlineLoading description="Loading landing analyses…" />
        ) : (
          <>
            {state.runDetailFailed ? (
              <ActionableNotification
                className={styles.banner}
                kind="error"
                inline
                lowContrast
                hideCloseButton
                hasFocus={false}
                title={banner.title}
                subtitle={banner.subtitle}
                actionButtonLabel="Retry"
                onActionButtonClick={() => {
                  state.retryRunDetail();
                }}
              />
            ) : (
              <InlineNotification
                className={styles.banner}
                kind={banner.kind}
                lowContrast
                hideCloseButton
                title={banner.title}
                subtitle={banner.subtitle}
              />
            )}

            {ready && (
              <div className={styles.pickerRow}>
                <div className={styles.picker}>
                  <Dropdown<LandingCollectionOption>
                    id="landing-structure-grid-collection"
                    size="sm"
                    titleText="Structure Grid Collections"
                    label="Choose a collection"
                    items={state.collections}
                    itemToString={(item) => item?.name ?? ''}
                    itemToElement={renderCollection}
                    renderSelectedItem={renderCollection}
                    selectedItem={state.collections.find(
                      (option) => option.id === state.collectionId
                    )}
                    disabled={readOnly || appliedId !== null}
                    onChange={({ selectedItem }) => {
                      if (selectedItem) state.selectCollection(selectedItem.id);
                    }}
                  />
                </div>
                <div className={styles.picker}>
                  <Dropdown<WellLandingAnalysisInstance>
                    id="landing-analysis-run"
                    size="sm"
                    titleText="Landing Analysis"
                    label="Choose an analysis"
                    items={state.collectionRuns}
                    itemToString={(item) => (item ? runLabel(item) : '')}
                    itemToElement={renderRun}
                    renderSelectedItem={renderRun}
                    selectedItem={run}
                    disabled={readOnly || appliedId !== null}
                    onChange={({ selectedItem }) => {
                      if (selectedItem) state.selectRun(selectedItem.id);
                    }}
                  />
                </div>
              </div>
            )}

            {state.runDetailLoading && (
              <InlineLoading description="Loading the landing analysis…" />
            )}

            {availability !== 'no-depths' &&
              !state.runsFailed &&
              !state.runDetailLoading &&
              !state.runDetailFailed && (
                <IntervalCardDeck
                  cards={state.cards}
                  selectedId={selectedId}
                  appliedId={appliedId}
                  readOnly={readOnly}
                  onSelect={state.stage}
                  lead={lead || undefined}
                  catalogCard={
                    ready
                      ? {
                          heading: 'Need a different interval',
                          description:
                            'The cards beside this one are the intervals the wellbore passes through. Pick from the full catalog to attribute it somewhere else.',
                          buttonText: 'Select Geologic Interval',
                          onOpen: openCatalog,
                        }
                      : {
                          heading:
                            selectedId === null
                              ? 'No landing interval assigned'
                              : 'Need a different landing interval',
                          description:
                            'Select a landing interval from the geologic-interval catalog to attribute this wellbore by hand.',
                          buttonText: 'Select Geologic Interval',
                          onOpen: openCatalog,
                        }
                  }
                />
              )}
          </>
        )}
      </div>
    </section>
  );
};
